import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Info, Minus, Plus } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const APP_API = "https://kiltedcouriers.app/api/public";

const PICKUPS = ["Mitchell", "Belconnen", "Civic", "Phillip"];
const TOWNS = [
  "Queanbeyan", "Michelago", "Bredbo", "Cooma", "Berridale",
  "Jindabyne", "East Jindabyne", "Kalkite", "Moonbah", "Hilltop",
];
const SIZES = [
  { key: "small", label: "Small" },
  { key: "medium", label: "Medium" },
  { key: "large", label: "Large" },
];
const SERVICES = [
  { key: "standard", label: "Standard" },
  { key: "express", label: "Priority" },
  { key: "sameday", label: "Urgent" },
];

const toOptions = (raw, fallback) => {
  if (!Array.isArray(raw) || !raw.length) return fallback;
  const opts = raw
    .map((x) => {
      if (typeof x === "string") return { key: x.toLowerCase().replace(/\s+/g, ""), label: x, short: x, desc: "" };
      const label = String(x.label ?? x.name ?? x.key ?? "");
      const [short, ...rest] = label.split(" — ");
      return {
        key: String(x.key ?? x.value ?? "").toLowerCase(),
        label,
        short,
        desc: x.description ?? rest.join(" — "),
      };
    })
    .filter((o) => o.key && o.label);
  return opts.length ? opts : fallback;
};

const Pill = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full px-4 py-2 font-display text-sm font-bold uppercase tracking-wider transition-colors duration-300 ${
      active
        ? "bg-[#F0A829] text-[#0C1F17]"
        : "border border-white/15 text-[#B9C4B4] hover:border-[#F0A829]/60 hover:text-[#F6F1E3]"
    }`}
  >
    {children}
  </button>
);

export default function QuickQuote() {
  const [sizes, setSizes] = useState(SIZES);
  const [services, setServices] = useState(SERVICES);
  const [pickup, setPickup] = useState("Civic");
  const [dropoff, setDropoff] = useState("Cooma");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [size, setSize] = useState("small");
  const [service, setService] = useState("standard");
  const [qty, setQty] = useState(1);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${APP_API}/pricing`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setSizes((prev) => toOptions(d.sizes ?? d.size_labels, prev));
        setServices((prev) => toOptions(d.services ?? d.service_labels, prev));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const from = customFrom.trim() || pickup;
    const to = customTo.trim() || dropoff;
    const t = setTimeout(() => {
      setLoading(true);
      fetch(`${APP_API}/quote?` + new URLSearchParams({ from, to, size, service, quantity: String(qty) }))
        .then(async (r) => {
          const q = await r.json().catch(() => ({}));
          if (!r.ok) {
            const detail = q.detail && q.detail !== "Not Found" ? q.detail : "Quote service is starting up — try again shortly.";
            setError(detail);
            setQuote(null);
          } else {
            setQuote(q);
            setError(null);
          }
        })
        .catch(() => {
          setError("Couldn't reach the quote service — try again shortly.");
          setQuote(null);
        })
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [pickup, dropoff, customFrom, customTo, size, service, qty]);

  const fromLabel = customFrom.trim() || pickup;
  const toLabel = customTo.trim() || dropoff;

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[#F0A829]/30 bg-gradient-to-br from-[#122A1F] to-[#0C1F17] p-7 sm:p-12">
            <div className="tartan-bg absolute inset-0 opacity-[0.07]" />
            <div className="absolute inset-0 bg-[radial-gradient(700px_320px_at_12%_110%,rgba(30,158,90,0.14),transparent_60%)]" />

            <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <Eyebrow>Quick quote</Eyebrow>
                <h2 className="font-display mt-4 text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#F6F1E3] sm:text-5xl">
                  What&apos;ll it cost<br />
                  <span className="text-outline-gold">to run it up?</span>
                </h2>

                <div className="mt-8">
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#B9C4B4]">Pickup — Canberra (7 days)</p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {PICKUPS.map((p) => (
                      <Pill key={p} testid={`quote-pickup-${p.toLowerCase()}`} active={pickup === p && !customFrom.trim()} onClick={() => { setPickup(p); setCustomFrom(""); }}>{p}</Pill>
                    ))}
                  </div>
                  <input
                    data-testid="quote-custom-from"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    placeholder="Or type any suburb / postcode…"
                    className="mt-3 w-full max-w-sm rounded-full border border-white/15 bg-[#0C1F17]/60 px-5 py-2.5 text-sm text-[#F6F1E3] placeholder-[#B9C4B4]/50 outline-none transition-colors duration-300 focus:border-[#F0A829]"
                  />
                </div>

                <div className="mt-6">
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#B9C4B4]">Drop-off — Monaro, Snowies &amp; beyond</p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {TOWNS.map((d) => (
                      <Pill key={d} testid={`quote-dropoff-${d.toLowerCase().replace(/\s+/g, "-")}`} active={dropoff === d && !customTo.trim()} onClick={() => { setDropoff(d); setCustomTo(""); }}>{d}</Pill>
                    ))}
                  </div>
                  <input
                    data-testid="quote-custom-to"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    placeholder="Or type any suburb / postcode…"
                    className="mt-3 w-full max-w-sm rounded-full border border-white/15 bg-[#0C1F17]/60 px-5 py-2.5 text-sm text-[#F6F1E3] placeholder-[#B9C4B4]/50 outline-none transition-colors duration-300 focus:border-[#F0A829]"
                  />
                </div>

                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#B9C4B4]">Parcel size</p>
                    <div className="mt-3 flex flex-wrap gap-2.5">
                      {sizes.map((s) => (
                        <Pill key={s.key} testid={`quote-size-${s.key}`} active={size === s.key} onClick={() => setSize(s.key)}>{s.short || s.label}</Pill>
                      ))}
                    </div>
                    <p className="mt-2 min-h-4 text-xs text-[#B9C4B4]" data-testid="quote-size-hint">
                      {(sizes.find((s) => s.key === size) || {}).desc || ""}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#B9C4B4]">Service</p>
                    <div className="mt-3 flex flex-wrap gap-2.5">
                      {services.map((s) => (
                        <Pill key={s.key} testid={`quote-service-${s.key}`} active={service === s.key} onClick={() => setService(s.key)}>{s.short || s.label}</Pill>
                      ))}
                    </div>
                    <p className="mt-2 min-h-4 text-xs text-[#B9C4B4]" data-testid="quote-service-hint">
                      {(services.find((s) => s.key === service) || {}).desc || ""}
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#B9C4B4]">Quantity</p>
                  <div className="mt-3 inline-flex items-center gap-4 rounded-full border border-white/15 bg-[#0C1F17]/60 px-4 py-2">
                    <button
                      data-testid="quote-qty-minus"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-white/15 text-[#B9C4B4] transition-colors duration-300 hover:border-[#F0A829] hover:text-[#F0A829]"
                      aria-label="Fewer parcels"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span data-testid="quote-qty-count" className="font-display w-6 text-center text-lg font-bold text-[#F6F1E3]">
                      {qty}
                    </span>
                    <button
                      data-testid="quote-qty-plus"
                      onClick={() => setQty((q) => Math.min(20, q + 1))}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-white/15 text-[#B9C4B4] transition-colors duration-300 hover:border-[#F0A829] hover:text-[#F0A829]"
                      aria-label="More parcels"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-between rounded-2xl border border-white/10 bg-[#0C1F17]/80 p-7 lg:col-span-5">
                <div>
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-[#B9C4B4]">
                    {fromLabel} → {toLabel}
                  </p>

                  {error ? (
                    <p data-testid="quote-error" className="mt-6 rounded-xl border border-[#C8102E]/50 bg-[#C8102E]/15 px-5 py-4 text-sm leading-relaxed text-[#FF8A9A]">
                      {error}
                    </p>
                  ) : (
                    <>
                      <div className={`mt-5 flex items-end gap-2 transition-opacity duration-300 ${loading ? "opacity-40" : ""}`}>
                        <AnimatePresence mode="popLayout">
                          <motion.span
                            key={quote ? quote.total : "empty"}
                            data-testid="quote-price"
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -14 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="font-display text-7xl font-extrabold tracking-tight text-[#F0A829]"
                          >
                            {quote ? `$${quote.total.toFixed(2)}` : "$—"}
                          </motion.span>
                        </AnimatePresence>
                      </div>
                      <p className="font-mono-brand mt-1 text-[10px] uppercase tracking-[0.2em] text-[#B9C4B4]">
                        AUD total · GST &amp; this week&apos;s fuel levy included
                      </p>
                      {quote?.note && (
                        <p data-testid="quote-note" className="mt-4 text-sm leading-relaxed text-[#B9C4B4]">
                          {quote.note}
                        </p>
                      )}
                    </>
                  )}

                  <ul className="mt-6 flex flex-col gap-2 border-t border-white/10 pt-5 text-xs leading-relaxed text-[#B9C4B4]">
                    <li className="flex gap-2">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#F0A829]" />
                      Live town-to-town price from the app — the exact figure is confirmed once you enter street addresses.
                    </li>
                    <li className="flex gap-2">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#F0A829]" />
                      No card surcharge.
                    </li>
                    <li className="flex gap-2">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#F0A829]" />
                      Free cancellation before pickup.
                    </li>
                    <li className="flex gap-2">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#F0A829]" />
                      Photo proof of delivery.
                    </li>
                  </ul>
                </div>

                <a
                  data-testid="quote-book-button"
                  href={quote?.book_url || "https://kiltedcouriers.app"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group mt-7 flex items-center justify-center gap-2.5 rounded-full px-7 py-4 font-display text-base font-bold uppercase tracking-wider transition-transform duration-300 ${
                    quote
                      ? "bg-[#F0A829] text-[#0C1F17] hover:-translate-y-0.5"
                      : "cursor-not-allowed bg-[#F0A829]/30 text-[#0C1F17]/50"
                  }`}
                >
                  Book now
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={2.5} />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
